import { useEffect, useState } from 'react';
import useCourseApi from 'Hooks/useCourseApi';
import useTranslation from 'Hooks/useTranslation';
import useCourseEmployeesReportApi from 'Hooks/useCourseEmployeesReportApi';
import { EmployeesByCourseReportFilterI } from 'types';
import { reportsApi } from 'Api/axios/reports-api';
import coursesApi from 'Api/axios/courses-api';
import { downloadFile } from 'Utils/file-utils';
import { toast } from 'sonner';

const useCourseEmployeesPage = (courseId: string) => {
  const { t2 } = useTranslation('course_employees_report');
  const [currentFilters, setCurrentFilters] = useState<EmployeesByCourseReportFilterI>({});
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);

  const {
    data: courseEmployees,
    pagination,
    loading,
    error,
    totals,
    fetchCourseEmployees,
    fetchCourseEmployeesTotals,
  } = useCourseEmployeesReportApi({ courseId, init: false });

  const { course, fetchCourseById } = useCourseApi();

  useEffect(() => {
    fetchCourseEmployees(courseId, { ...currentFilters, page, pageSize });
  }, [courseId, page, pageSize, currentFilters]);

  useEffect(() => {
    fetchCourseById(courseId);
    fetchCourseEmployeesTotals(courseId);
  }, [courseId]);

  const onSearch = (filters: EmployeesByCourseReportFilterI) => {
    setPage(1);
    setCurrentFilters(filters);
  };

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (newPageSize: number) => {
    setPage(1);
    setPageSize(newPageSize);
  };

  const onDownloadCertificate = async (courseId: string, employeeId: string, certificationId: string) => {
    const response = await coursesApi.downloadCertificate(courseId, employeeId, certificationId);
    if (response.data) {
      const employee = courseEmployees.find(employee => employee.id === employeeId);
      downloadFile(response.data, `${t2('certificate')}_${course?.name}_${employee?.fullName}.pdf`);
      toast.success(t2('certificate_downloaded'));
    }
  };

  const onExportExcel = async () => {
    const response = await reportsApi.exportEmployeesByCourse(courseId, currentFilters);
    if (response.data) {
      downloadFile(response.data, `${t2('course_employees')}_${course?.name}.xlsx`);
      toast.success(t2('course_employees_exported'));
    }
  };

  return {
    t2,
    course,
    totals,
    onSearch,
    onExportExcel,
    courseEmployees,
    pagination,
    loading,
    error,
    handlePageChange,
    handleRowsPerPageChange,
    onDownloadCertificate,
  };
};

export default useCourseEmployeesPage;
